import { inject } from '@angular/core';
import { CanActivateChildFn, RedirectCommand, Router, UrlTree } from '@angular/router';
import { CountryDomainService } from './country-domain.service';
import { CountryCode } from './country.model';
import { AlertsService } from '../services/alerts.service';

/**
 * Query param that opens the SPA on a given country — `?country=RU`. Admin is one app for every
 * country, and the active one otherwise lives only in `localStorage`, so a link emitted by another
 * service (a support e-mail, a chat-stats row) has no other way to say which country's customer it
 * points at. Customer ids are per-country, so the same id opened in the wrong country is a
 * different person — which is why a link that cannot be honoured says so instead of failing quietly.
 */
export const COUNTRY_QUERY_PARAM = 'country';

/** Case-insensitive match against {@link CountryCode}; `null` for anything else. */
export function parseCountryCode(value: string | null | undefined): CountryCode | null {
    const normalized = value?.trim().toUpperCase();
    return normalized && (Object.values(CountryCode) as string[]).includes(normalized)
        ? (normalized as CountryCode)
        : null;
}

/**
 * Applies a `?country=` deep link as the active country, then drops the param from the URL with a
 * `replaceUrl` redirect that keeps the rest of the path and query.
 *
 * Registered as `canActivateChild` on the authenticated shell, which is what makes the order with
 * `provideCountryAccess` right: the router runs the shell's own `canActivate` (`authGuard`) before
 * any `canActivateChild` it declares, so by the time this runs the session is resolved and the
 * staff member's `countries` claim has already narrowed `allowedCountries`. Applying the param
 * earlier (at bootstrap, or for an anonymous visitor) would either be clobbered by that narrowing or
 * test it against the not-yet-narrowed "every country" seed. An anonymous visitor never gets here —
 * `authGuard` sends them to the login page with the full URL as the return URL, and the param is
 * applied on the way back in.
 *
 * Covers links that START a navigation into the shell — an external link or the post-login return.
 * Like the customers list's `?phone=`, it is not re-read on an in-app navigation that changes only
 * the query of an already active route (`runGuardsAndResolvers` defaults to params-only), so an
 * in-app link should switch the country itself rather than rely on this param.
 *
 * A country outside the staff member's assignment is not applied (`setCountry` would refuse it
 * anyway); the current country stays and the operator is told, because the page they land on is
 * then showing the active country's data, not the one the link meant.
 */
export const countryQueryParamGuard: CanActivateChildFn = (_childRoute, state) => {
    const router = inject(Router);
    const tree = router.parseUrl(state.url);
    if (!tree.queryParamMap.has(COUNTRY_QUERY_PARAM)) {
        return true;
    }

    const countryDomain = inject(CountryDomainService);
    const raw = tree.queryParamMap.get(COUNTRY_QUERY_PARAM)?.trim() ?? '';
    const requested = parseCountryCode(raw);
    if (requested && countryDomain.allowedCountries.includes(requested)) {
        countryDomain.setCountry(requested);
    } else if (requested) {
        inject(AlertsService).error(
            `You do not have access to ${requested}; the page is showing ${countryDomain.activeCountry} instead.`);
    } else if (raw) {
        // An unrecognised code (`GB` for `UK`, a typo, a country the emitter knows first) is the
        // same wrong-person hazard as an unassigned one, so it is reported just as loudly.
        inject(AlertsService).error(
            `Unknown country "${raw}" in the link; the page is showing ${countryDomain.activeCountry} instead.`);
    }

    const queryParams = { ...tree.queryParams };
    delete queryParams[COUNTRY_QUERY_PARAM];
    return new RedirectCommand(new UrlTree(tree.root, queryParams, tree.fragment), { replaceUrl: true });
};
